/**
 * Adapter K1 for PEOPLE DIRECTORY: people + membership -> tabular
 * plano exportable (CSV/PDF).
 *
 * Why it exists: spec §6 promises congregation data "never hides
 * behind a plan" and monetization kit provides `export` in ALL tiers...
 * but promise only holds if there is an exit door for data that
 * really matters. Finances and inventory already exported; the directory —the
 * asset church never wants locked in— had nowhere to
 * exit. This adapter is that door.
 *
 * DOM-free and pure: tested without browser (delivery handled by
 * `exportDatasetAsCsv` from kit, which touches DOM).
 */

import {
  buildDataset,
  type ExportColumn,
  type ExportDataset,
  type ExportMetadata,
} from "../export";

/** Directory row, flattened with membership details. */
export interface PersonExportRow {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  birthdate?: string | null;
  notes?: string | null;
  /** Church name of person (resolved by caller). */
  churchName?: string | null;
  /** Membership status: visitor / believer / member... */
  status?: string | null;
  baptized?: boolean | null;
  baptized_at?: string | null;
  serves?: boolean | null;
  is_leader?: boolean | null;
  is_pastor?: boolean | null;
}

/** `null`/`undefined` -> "" (empty cell, not string "null"). */
function text(value: string | null | undefined): string {
  return value ?? "";
}

/** Date portion only: ISO with time clutters spreadsheets. */
function dateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : "";
}

export function peopleExportColumns(): ExportColumn<PersonExportRow>[] {
  return [
    { key: "name", header: "Name", accessor: (r) => text(r.name) },
    { key: "church", header: "Church", accessor: (r) => text(r.churchName) },
    { key: "email", header: "Email", accessor: (r) => text(r.email) },
    { key: "phone", header: "Phone", accessor: (r) => text(r.phone) },
    { key: "address", header: "Address", accessor: (r) => text(r.address) },
    { key: "birthdate", header: "Birthdate", accessor: (r) => dateOnly(r.birthdate) },
    { key: "status", header: "Status", accessor: (r) => text(r.status) },
    { key: "baptized", header: "Baptized", accessor: (r) => r.baptized ?? false },
    { key: "baptizedAt", header: "Baptized on", accessor: (r) => dateOnly(r.baptized_at) },
    { key: "serves", header: "Serves", accessor: (r) => r.serves ?? false },
    { key: "leader", header: "Leader", accessor: (r) => r.is_leader ?? false },
    { key: "pastor", header: "Pastor", accessor: (r) => r.is_pastor ?? false },
    { key: "notes", header: "Notes", accessor: (r) => text(r.notes) },
  ];
}

export function buildPeopleDataset(
  rows: readonly PersonExportRow[],
  metadata: ExportMetadata = {},
): ExportDataset {
  return buildDataset(peopleExportColumns(), rows, {
    title: metadata.title ?? "Directory",
    ...metadata,
  });
}
